import { FLAGS } from '@gozoki/feature-flags'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { type JSX, useCallback, useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import {
  type DashboardCardLoadState,
  resolveDashboardCardState,
  retryFailedDashboardQueries,
} from '~/components/dashboards/dashboard-card-state'
import { Button } from '~/components/ui/button'
import { InfoTooltip } from '~/components/ui/info-tooltip'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { BoutiqueChartModal, type BoutiqueScope } from './-boutique-chart-modal'
import { PREVISIONS_QUERY_OPTIONS } from './-previsions-query'
import { BoutiquesCard } from './-boutiques-card'
import { CategoriesTable } from './-categories-table'
import { FiltersBar } from './-filters-bar'
import {
  buildCategoryTree,
  buildStoreRows,
  buildTotals,
  type ForecastCategory,
} from './-previsions-data'
import { dayAndMonth, FORECAST_METHOD_LINES } from './-previsions-display'
import { PrevisionsCardBoundary, PrevisionsCardSkeleton } from './-previsions-skeleton'
import { ProductionChart } from './-production-chart'
import {
  isDateInRange,
  previsionsSearchSchema,
  resolvePrevisionsRange,
  SEGMENT_CATEGORY_SOURCE,
  SEGMENT_GROUP_BY,
  type CategorySource,
  type Segment,
} from './-search-schema'
import { TotauxCard } from './-totaux-card'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  beforeLoad: ({ search }) => {
    if (!search.focusDate) return
    if (isDateInRange(search.focusDate, resolvePrevisionsRange(search))) return
    throw redirect({
      to: '/previsions',
      replace: true,
      search: { ...search, focusDate: undefined },
    })
  },
  component: PrevisionsProductionPage,
})

withRoutePermissions(Route)

const PAGE_CLASS = 'flex h-[calc(100vh-1rem)] min-h-0 flex-col'
const HEADER_CLASS = 'shrink-0'
const TITLE_CLASS = 'font-brand text-[32px] font-extrabold leading-[36px] text-[#072c1a]'
const CONTAINER_CLASS = 'min-h-0 gap-2 rounded-none border-0 bg-transparent p-0'
const GRID_COLUMNS = '376px minmax(0, 1fr)'

function PrevisionsProductionPage() {
  const enabled = useFeatureFlag(FLAGS.forecastingXiuDashboard)
  const configuratorEnabled = useFeatureFlag(FLAGS.configurateurIndustriel)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dailyScope, setDailyScope] = useState<BoutiqueScope | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const setFocusDate = useCallback(
    (focusDate: string | undefined) => navigate({ search: (prev) => ({ ...prev, focusDate }) }),
    [navigate],
  )

  const range = resolvePrevisionsRange(search)
  const focusDate = search.focusDate

  const storesQuery = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const stores = storesQuery.data ?? []
  const categoriesQuery = useQuery(
    api.forecast.productCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const categories = categoriesQuery.data ?? []
  const segmentCategoriesQuery = useQuery(
    api.forecast.segmentCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const kitchensQuery = useQuery(
    api.forecast.centralKitchens.queryOptions(
      {},
      { enabled: enabled && configuratorEnabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )

  const axisSource = SEGMENT_CATEGORY_SOURCE[search.segment]
  const axisCategoriesQuery = axisSource === 'commercial' ? categoriesQuery : segmentCategoriesQuery
  const categoriesBySource: Record<CategorySource, ForecastCategory[]> = {
    commercial: categories,
    internal: segmentCategoriesQuery.data?.internal ?? [],
    reappro: segmentCategoriesQuery.data?.reappro ?? [],
  }
  const axisCategories = categoriesBySource[axisSource]

  const setSegment = (segment: Segment) => {
    if (segment === search.segment) return
    setSearch({ segment, categoryIds: [] })
  }

  const filtersByCategory = search.categoryIds.length > 0
  const selectedCategories = axisCategories.filter((category) =>
    search.categoryIds.includes(category.id),
  )
  const productIds = selectedCategories.flatMap((category) =>
    category.products.map((product) => product.id),
  )
  const appliedCategoryIds =
    productIds.length > 0 ? selectedCategories.map((category) => category.id) : search.categoryIds

  const scopeLoading = filtersByCategory && axisCategoriesQuery.isPending
  const scopeFailed = filtersByCategory && axisCategoriesQuery.isError
  const scopeEmpty = filtersByCategory && !axisCategoriesQuery.isPending && productIds.length === 0

  const scopedStoreIds =
    search.storeIds.length > 0 ? search.storeIds : stores.map((store) => store.storeId)

  const kitchens = kitchensQuery.data ?? []
  const selectedKitchenIds = configuratorEnabled ? search.kitchenIds : []
  const filtersByKitchen = selectedKitchenIds.length > 0
  const appliedKitchenIds = selectedKitchenIds.filter((kitchenId) =>
    kitchens.some((kitchen) => kitchen.id === kitchenId),
  )

  const kitchenScopeLoading = filtersByKitchen && kitchensQuery.isPending
  const kitchenScopeFailed = filtersByKitchen && kitchensQuery.isError
  const kitchenScopeEmpty =
    filtersByKitchen && kitchensQuery.isSuccess && appliedKitchenIds.length === 0

  const queriesEnabled =
    enabled &&
    scopedStoreIds.length > 0 &&
    !scopeLoading &&
    !scopeEmpty &&
    !kitchenScopeLoading &&
    !kitchenScopeEmpty

  const scopeQuery = {
    storeIds: scopedStoreIds,
    ...(productIds.length > 0 ? { productIds } : {}),
    ...(appliedKitchenIds.length > 0 ? { centralKitchenIds: appliedKitchenIds } : {}),
  }

  const summaryRange = focusDate ? { start: focusDate, end: focusDate } : range

  const forecastQuery = useQuery(
    api.forecast.shortTermSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: summaryRange.start,
          endDate: summaryRange.end,
          capping: search.capping,
        },
      },
      { enabled: queriesEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const seriesQuery = useQuery(
    api.forecast.shortTermDailySeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: range.start,
          endDate: range.end,
          groupBy: SEGMENT_GROUP_BY[search.segment],
          capping: search.capping,
        },
      },
      { enabled: queriesEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))

  const forecastRows = forecastQuery.data?.products ?? []
  const storeRows = buildStoreRows(forecastQuery.data?.stores ?? [], storeLabels)
  const zeroShareProducts = forecastQuery.data?.zeroShareProducts

  const referenceDateFor = (state: DashboardCardLoadState): string | undefined =>
    state === 'transitioning' ? undefined : focusDate

  const totauxState = resolveDashboardCardState(forecastQuery)
  const boutiquesState = resolveDashboardCardState(forecastQuery, storesQuery)
  const categoriesState = resolveDashboardCardState(forecastQuery, axisCategoriesQuery)
  const chartState = resolveDashboardCardState(seriesQuery, axisCategoriesQuery)

  const withEmptyDayNotice = (emptyDate: string | undefined, children: JSX.Element) => {
    if (emptyDate === undefined) return children
    return (
      <>
        <div className="flex shrink-0 items-center gap-2">
          <p className="text-muted-foreground">
            {`Aucune production prévue le ${dayAndMonth(emptyDate)} sur ce périmètre.`}
          </p>
          <Button variant="outline" size="sm" onClick={() => setFocusDate(undefined)}>
            Revenir à la période complète
          </Button>
        </div>
        {children}
      </>
    )
  }

  const grid = (children: JSX.Element) => (
    <div
      className="grid min-h-0 flex-1 gap-2"
      style={{ gridTemplateColumns: GRID_COLUMNS, gridTemplateRows: 'auto minmax(0, 1fr)' }}
    >
      {children}
    </div>
  )

  const content = () => {
    if (scopedStoreIds.length === 0 || scopeLoading || kitchenScopeLoading) {
      return grid(
        <>
          <PrevisionsCardSkeleton variant="totaux" />
          <PrevisionsCardSkeleton variant="chart" />
          <PrevisionsCardSkeleton variant="boutiques" />
          <PrevisionsCardSkeleton variant="categories" />
        </>,
      )
    }
    if (scopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des catégories : le filtre de l’URL ne peut pas être appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => axisCategoriesQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (scopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les catégories filtrées ne contiennent aucun produit actif : aucune prévision ne peut
            être calculée sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ categoryIds: [] })}>
            Réinitialiser les catégories
          </Button>
        </div>
      )
    }
    if (kitchenScopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des cuisines centrales : le filtre de l’URL ne peut pas être
            appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => kitchensQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (kitchenScopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les cuisines centrales filtrées n’existent plus dans le configurateur : aucune prévision
            ne peut être calculée sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ kitchenIds: [] })}>
            Réinitialiser les cuisines
          </Button>
        </div>
      )
    }
    if (forecastQuery.data && forecastRows.length === 0 && !focusDate) {
      return <p className="text-muted-foreground">Aucune production prévue sur ce périmètre.</p>
    }

    const emptyFocusedDay =
      focusDate !== undefined && forecastQuery.data !== undefined && forecastRows.length === 0

    return withEmptyDayNotice(
      emptyFocusedDay ? focusDate : undefined,
      grid(
        <>
          <PrevisionsCardBoundary
            state={totauxState}
            variant="totaux"
            tone="totaux"
            errorMessage="Totaux indisponibles"
            onRetry={() => retryFailedDashboardQueries(forecastQuery)}
          >
            <TotauxCard
              totals={buildTotals(forecastRows)}
              storeCount={storeRows.length}
              referenceDate={referenceDateFor(totauxState)}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={chartState}
            variant="chart"
            tone="chart"
            errorMessage="Séries indisponibles"
            onRetry={() => retryFailedDashboardQueries(seriesQuery, axisCategoriesQuery)}
          >
            <ProductionChart
              series={seriesQuery.data?.series ?? []}
              categories={axisCategories}
              measure={search.measure}
              segment={search.segment}
              onMeasureChange={(measure) => setSearch({ measure })}
              focusedDate={focusDate}
              onFocusDateChange={setFocusDate}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={boutiquesState}
            variant="boutiques"
            tone="neutral"
            errorMessage="Boutiques indisponibles"
            onRetry={() => retryFailedDashboardQueries(forecastQuery, storesQuery)}
          >
            <BoutiquesCard
              stores={storeRows}
              onShowDaily={setDailyScope}
              referenceDate={referenceDateFor(boutiquesState)}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={categoriesState}
            variant="categories"
            tone="neutral"
            errorMessage="Catégories indisponibles"
            onRetry={() => retryFailedDashboardQueries(forecastQuery, axisCategoriesQuery)}
          >
            <CategoriesTable
              categories={buildCategoryTree(
                forecastRows,
                axisCategories,
                search.segment === 'uf' ? 'commercial' : search.segment,
              )}
              rows={forecastRows}
              storeCount={storeRows.length}
              segment={search.segment}
              zeroShareProducts={zeroShareProducts}
              referenceDate={referenceDateFor(categoriesState)}
            />
          </PrevisionsCardBoundary>
        </>,
      ),
    )
  }

  if (!enabled) {
    return (
      <div className={PAGE_CLASS}>
        <AppHeader className={HEADER_CLASS}>
          <Title className={TITLE_CLASS}>Prévisions</Title>
        </AppHeader>
        <AppContainer className={CONTAINER_CLASS}>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </div>
    )
  }

  return (
    <div className={PAGE_CLASS}>
      <AppHeader className={HEADER_CLASS}>
        <div className="flex items-center gap-2">
          <Title className={TITLE_CLASS}>Prévisions</Title>
          <InfoTooltip label="Prévisions" iconClassName="size-5" side="bottom">
            <div className="flex flex-col gap-1.5">
              {FORECAST_METHOD_LINES.map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </InfoTooltip>
        </div>
      </AppHeader>
      <AppContainer className={CONTAINER_CLASS}>
        <FiltersBar
          storeIds={scopedStoreIds}
          onStoreIdsChange={(storeIds) => setSearch({ storeIds })}
          range={range}
          onRangeChange={({ start, end }) =>
            setSearch({ startDate: start, endDate: end, focusDate: undefined })
          }
          segment={search.segment}
          onSegmentChange={setSegment}
          categoryIds={appliedCategoryIds}
          onCategoryIdsChange={(categoryIds) => setSearch({ categoryIds })}
          categories={axisCategories.filter((category) => category.products.length > 0)}
          capping={search.capping}
          onCappingChange={(capping) => setSearch({ capping })}
          kitchenIds={selectedKitchenIds}
          onKitchenIdsChange={(nextKitchenIds) => setSearch({ kitchenIds: nextKitchenIds })}
          kitchens={kitchens}
          kitchensEnabled={configuratorEnabled}
          kitchensPending={kitchensQuery.isPending}
          kitchensFailed={kitchensQuery.isError}
        />
        {content()}

        {dailyScope && (
          <BoutiqueChartModal
            scope={dailyScope}
            range={range}
            productIds={productIds}
            centralKitchenIds={appliedKitchenIds}
            categories={axisCategories}
            measure={search.measure}
            segment={search.segment}
            capping={search.capping}
            onClose={() => setDailyScope(null)}
          />
        )}
      </AppContainer>
    </div>
  )
}
